import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useStyles from './styles';

/** MUI Typography variant strings for type safety */
type TypographyVariant = React.ComponentProps<typeof Typography>['variant'];

/**
 * Props for the SectionHeader component.
 */
export interface SectionHeaderProps {
  /** Section title text. */
  title: string;
  /** Typography variant for the title (style only). Default: `h2`. */
  titleVariant?: TypographyVariant;
  /** HTML element or component for the title (semantics). Override when you want different style vs element, e.g. h6 style but render as h2. */
  titleComponent?: React.ElementType;
  /** Optional icon node shown to the left of the title. */
  icon?: React.ReactNode;
  /** Optional description below the title (string or rich content, e.g. links). */
  description?: string | React.ReactNode;
  /** Typography variant for the description (style only). Default: `body1`. */
  descriptionVariant?: TypographyVariant;
  /** HTML element or component for the description. Override when you want different style vs element, e.g. body2 style but render as span. */
  descriptionComponent?: React.ElementType;
  /** When true, render a full-width Divider below the description. Default: false. */
  divider?: boolean;
}

/**
 * SectionHeader Component
 *
 * Renders a section heading (semantic <header>) with optional icon to the left of the title,
 * optional description (string or custom node, e.g. inline links), and optional divider.
 * String descriptions use typography variant/component; React nodes render as-is.
 */
const SectionHeader = ({
  title,
  titleVariant = 'h2',
  titleComponent,
  icon,
  description,
  descriptionVariant = 'body1',
  descriptionComponent,
  divider = true,
}: SectionHeaderProps) => {
  const { classes } = useStyles();

  return (
    <Box component="header" className={classes.root} data-component="SectionHeader">
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        className={classes.titleStack}
      >
        {icon != null ? <span className={classes.iconWrapper}>{icon}</span> : null}
        <Box className={classes.titleWrapper}>
          <Typography
            variant={titleVariant}
            component={titleComponent}
            className={classes.title}
          >
            {title}
          </Typography>
        </Box>
      </Stack>
      {description != null &&
        (typeof description === 'string' ? description !== '' : true) &&
        (typeof description === 'string' ? (
          <Typography variant={descriptionVariant} component={descriptionComponent}>
            {description}
          </Typography>
        ) : (
          description
        ))}
      {divider && <Divider variant="fullWidth" />}
    </Box>
  );
};

export default SectionHeader;
